import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useBluelagoon } from "@bluelagoon";

export default function useCopyWorkflow() {
    const { t } = useTranslation();
    const { call: duplicate } = useBluelagoon({ method: "duplicateWorkflow" });
    const { call: createBlueprint } = useBluelagoon({ method: "createBlueprint" });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const inFlightRef = useRef(false);

    const submit = async ({ workflow, mode, name }) => {
        if (!workflow || !mode) return null;
        if (inFlightRef.current) return null;
        inFlightRef.current = true;
        setIsSubmitting(true);
        try {
            if (mode === "duplicate") {
                const res = await duplicate({
                    workflowId: workflow.id,
                    name: name || `${workflow.name} (Copy)`,
                });
                const data = res?.data?.data || res?.data;
                if (data?.workflowId) {
                    toast.success(t("workflowIndex.copy.toasts.duplicated"));
                    return data;
                }
                toast.error(t("workflowIndex.copy.toasts.duplicateFailed"));
                return null;
            }

            const res = await createBlueprint({
                name: name || `${workflow.name} Blueprint`,
                description: `Blueprint created from workflow: ${workflow.name}`,
                type: "WORKFLOW",
                visibility: "PRIVATE",
                price: 0,
                workflowId: workflow.id,
                isMaintainable: mode === "maintainable",
            });
            const data = res?.data?.data || res?.data;
            if (data) {
                toast.success(
                    t(
                        mode === "maintainable"
                            ? "workflowIndex.copy.toasts.maintainableCreated"
                            : "workflowIndex.copy.toasts.blueprintCreated"
                    )
                );
                return data;
            }
            toast.error(t("workflowIndex.copy.toasts.blueprintFailed"));
            return null;
        } catch (error) {
            toast.error(error?.message || t("workflowIndex.copy.toasts.failed"));
            return null;
        } finally {
            setIsSubmitting(false);
            inFlightRef.current = false;
        }
    };

    return { submit, isSubmitting };
}
